import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';

interface PriceInputProps {
  /** Price value in cents (integer). Use valueCents for cent-based storage. */
  valueCents?: number | null;
  /** Price value in euros (float). Use valueEuros for euro-based storage. */
  valueEuros?: number | string;
  /** Called with value in cents */
  onChangeCents?: (cents: number) => void;
  /** Called with value in euros (number) */
  onChangeEuros?: (euros: number) => void;
  /** Called with value in euros (string, for controlled string state) */
  onChangeEurosString?: (euros: string) => void;
  /** Compact mode for inline usage */
  compact?: boolean;
  /** Additional className for the wrapper */
  className?: string;
  /** Placeholder text */
  placeholder?: string;
  /** Visual variant */
  variant?: 'default' | 'green' | 'accent';
  /** Show € suffix inside the input */
  showEuro?: boolean;
  /** Label to show before the input (e.g., "Vente") */
  label?: string;
}

/**
 * Price input component optimized for keyboard entry.
 * - Uses text input with inputMode="decimal" (no stepper arrows)
 * - Large, touch-friendly
 * - Auto-formats to 2 decimal places on blur
 * - Select-all on focus for quick replacement
 * - Handles cents <-> euros conversion
 */
const PriceInput = forwardRef<HTMLInputElement, PriceInputProps>(function PriceInput({
  valueCents,
  valueEuros,
  onChangeCents,
  onChangeEuros,
  onChangeEurosString,
  compact = false,
  className = '',
  placeholder = '0.00',
  variant = 'default',
  showEuro = true,
  label,
}, ref) {
  const [displayValue, setDisplayValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

  // Sync display value when not focused
  useEffect(() => {
    if (!isFocused) {
      if (valueCents != null) {
        setDisplayValue(valueCents > 0 ? (valueCents / 100).toFixed(2) : '');
      } else if (valueEuros != null && valueEuros !== '') {
        const num = typeof valueEuros === 'string' ? parseFloat(valueEuros) : valueEuros;
        setDisplayValue(num > 0 ? num.toFixed(2) : typeof valueEuros === 'string' ? valueEuros : '');
      } else {
        setDisplayValue('');
      }
    }
  }, [valueCents, valueEuros, isFocused]);

  const emitValue = (euroValue: number) => {
    if (onChangeCents) {
      onChangeCents(Math.round(euroValue * 100));
    }
    if (onChangeEuros) {
      onChangeEuros(euroValue);
    }
    if (onChangeEurosString) {
      onChangeEurosString(euroValue > 0 ? euroValue.toString() : '');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;

    // Allow: digits, one dot or comma, up to 2 decimals
    // Replace comma with dot for parsing
    const normalized = raw.replace(',', '.');

    // Allow empty
    if (normalized === '') {
      setDisplayValue('');
      emitValue(0);
      return;
    }

    // Allow partial input like "3." or "3.1"
    if (/^\d*\.?\d{0,2}$/.test(normalized)) {
      setDisplayValue(normalized);
      const parsed = parseFloat(normalized);
      if (!isNaN(parsed)) {
        emitValue(parsed);
      }
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
    // Select all on focus for easy replacement
    setTimeout(() => inputRef.current?.select(), 0);
  };

  const handleBlur = () => {
    setIsFocused(false);
    // Format to 2 decimal places on blur
    if (displayValue) {
      const parsed = parseFloat(displayValue.replace(',', '.'));
      if (!isNaN(parsed) && parsed > 0) {
        setDisplayValue(parsed.toFixed(2));
      } else {
        setDisplayValue('');
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      inputRef.current?.blur();
    }
    // Allow: Tab, Backspace, Delete, arrows, dot, comma, digits
    const allowed = ['Tab', 'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Home', 'End', '.', ','];
    if (!allowed.includes(e.key) && !/^\d$/.test(e.key)) {
      // Allow Cmd+A, Cmd+C, Cmd+V etc.
      if (!e.metaKey && !e.ctrlKey) {
        e.preventDefault();
      }
    }
  };

  // Variant styles
  const variantStyles = {
    default: {
      border: 'border-gray-300',
      bg: '',
      text: 'text-gray-900',
      focus: 'focus:ring-blue-500 focus:border-blue-500',
      labelColor: 'text-gray-600',
    },
    green: {
      border: 'border-green-400',
      bg: 'bg-green-50',
      text: 'text-green-800 font-semibold',
      focus: 'focus:ring-green-500 focus:border-green-500',
      labelColor: 'text-green-700',
    },
    accent: {
      border: 'border-blue-400',
      bg: 'bg-blue-50',
      text: 'text-blue-800 font-semibold',
      focus: 'focus:ring-blue-500 focus:border-blue-500',
      labelColor: 'text-blue-700',
    },
  };

  const style = variantStyles[variant];

  const sizeClasses = compact
    ? 'min-h-[40px] text-base px-2 py-1.5'
    : 'min-h-[48px] text-lg px-3 py-2.5';

  return (
    <div className={`relative flex items-center ${className}`}>
      {label && (
        <span className={`mr-1.5 text-sm font-medium ${style.labelColor} whitespace-nowrap`}>{label}</span>
      )}
      <div className="relative flex-1">
        <input
          ref={inputRef}
          type="text"
          inputMode="decimal"
          value={displayValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={`${sizeClasses} ${style.border} ${style.bg} ${style.text} border-2 rounded-lg
            font-mono tracking-wide
            focus:outline-none focus:ring-2 ${style.focus}
            w-full ${showEuro ? 'pr-8' : ''} transition-colors`}
        />
        {showEuro && (
          <span className={`absolute right-2.5 top-1/2 -translate-y-1/2 ${compact ? 'text-sm' : 'text-base'} font-semibold text-gray-400 pointer-events-none`}>
            €
          </span>
        )}
      </div>
    </div>
  );
});

export default PriceInput;
